import { buildActionUrl, buildUrlWithAuth } from './config/apiConfig'

function getUserId() {
  if (typeof window !== 'undefined') {
    const userData = window.localStorage?.getItem('userData')
    if (userData) {
      try {
        const parsed = JSON.parse(userData)
        return parsed?.id || null
      } catch {
        return null
      }
    }
  }
  return null
}

export class UserSerializer {
  get type() {
    return 'user'
  }

  static get endpoints() {
    return {
      GET: () => {
        // API v3: GET /user/rapidoo-rider/{userId}?group=ultraxeno
        const userId = getUserId()
        return buildUrlWithAuth(`/user/rapidoo-rider/${userId}`)
      },
      POST: ({ action }) => {
        if (action === 'login') {
          // API v3: POST /user/rapidoo-rider/login?group=ultraxeno
          return buildActionUrl('/user/rapidoo-rider/login')
        }
        if (action === 'register') {
          // API v3: POST /user/rapidoo-rider/register?group=ultraxeno
          return buildActionUrl('/user/rapidoo-rider/register')
        }
        if (action === 'update') {
          // API v3: POST /user/rapidoo-rider/update?group=ultraxeno
          return buildActionUrl('/user/rapidoo-rider/update')
        }
        return ''
      },
      PUT: () => {
        return ''
      },
      PATCH: () => {
        // API v3: POST /user/rapidoo-rider/update?group=ultraxeno
        return buildActionUrl('/user/rapidoo-rider/update')
      },
      DELETE: () => {
        return buildActionUrl('/user/rapidoo-rider/delete')
      },
    }
  }

  serialize(data) {
    if (data.action === 'login') {
      // API v3: { email, password }
      return {
        email: data.email?.toLowerCase(),
        password: data.password,
      }
    }

    if (data.action === 'register') {
      const payload = {
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: `${data.email}`.toLowerCase(),
        password: data.password,
        countryCode: data.locationCountry || 'BJ',
        phoneNumber: data.phoneNumber.startsWith('229')
          ? data.phoneNumber
          : `229${data.phoneNumber}`,
        metadata: {},
      }

      // Informations supplémentaires dans metadata
      if (data.phoneNumber) {
        payload.metadata.phoneNumber = data.phoneNumber
      }
      if (data.vehicleType) {
        payload.metadata.vehicleType = data.vehicleType
      }
      if (data.vehiclePlate) {
        payload.metadata.vehiclePlate = data.vehiclePlate
      }

      return payload
    }

    if (data.action === 'update') {
      // API v3: POST /user/rapidoo-rider/update
      // L'API exige toujours l'email ou l'userId pour identifier l'utilisateur
      const storedEmail = (() => {
        try {
          const raw = window.localStorage?.getItem('userData')
          return raw ? JSON.parse(raw)?.email : null
        } catch {
          return null
        }
      })()

      const payload = {
        email: data.email ? `${data.email}`.toLowerCase() : storedEmail,
        metadata: {},
      }

      if (data.firstName) {
        payload.metadata.firstName = data.firstName
      }
      if (data.lastName) {
        payload.metadata.lastName = data.lastName
      }
      if (data.phoneNumber) {
        payload.metadata.phoneNumber = data.phoneNumber
      }
      if (data.profilePicture) {
        payload.metadata.profilePicture = data.profilePicture
      }
      // Permettre de passer des metadata arbitraires (ex: riderId, birthday, etc.)
      if (data.metadata && typeof data.metadata === 'object') {
        Object.assign(payload.metadata, data.metadata)
      }

      return payload
    }

    return null
  }

  deserialize(raw, payload, method) {
    console.log('🚀 ~ UserSerializer ~ deserialize ~ raw:', raw)

    if (!raw || (raw.statusCode !== 200 && raw.statusCode !== 201)) {
      return {
        statusCode: raw?.statusCode || raw?.error?.statusCode || 500,
        message: raw?.message || raw?.error?.message || 'Erreur du serveur',
        user: null,
      }
    }

    // Traitement de la réponse selon l'action et la méthode
    if (method === 'POST') {
      if (payload?.action === 'register') {
        const u = raw.data || {}
        // Réponse d'inscription: { statusCode: 201, message: "User registered successfully", data: { id, email, metadata, firstName, lastName, ... } }
        return {
          statusCode: raw.statusCode || 201,
          message: raw.message || 'Compte créé avec succès',
          user: {
            id: u.id,
            userId: u.id,
            name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
            firstName: u.firstName || '',
            lastName: u.lastName || '',
            email: u.email?.toLowerCase(),
            countryCode: u.countryCode || null,
            phoneNumber: u.metadata?.phoneNumber || u.phoneNumber || '',
            password: null,
            status: u.status,
            vehicleType: u.metadata?.vehicleType,
            vehiclePlate: u.metadata?.vehiclePlate,
            profilePicture: u.metadata?.profilePicture,
            metadata: u.metadata || {},
            createdAt: u.createdAt,
            updatedAt: u.updatedAt,
            token: u.token || null,
          },
        }
      } else if (payload?.action === 'login') {
        const u = raw.data || {}
        if (u.metadata?.isDeleted) {
          return {
            statusCode: 403,
            message:
              "Votre compte a été supprimé. Veuillez contacter le support pour plus d'informations.",
            isDeleted: true,
            user: null,
          }
        }
        // Réponse de login: { statusCode: 200, message: "Login successful", data: { id, email, metadata, firstName, lastName, ... } }
        return {
          statusCode: raw.statusCode || 200,
          message: raw.message || 'Connexion réussie',
          user: {
            id: u.id,
            userId: u.id,
            name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
            firstName: u.firstName || '',
            lastName: u.lastName || '',
            email: u.email?.toLowerCase(),
            countryCode: u.countryCode || null,
            phoneNumber: u.metadata?.phoneNumber || u.phoneNumber || '',
            password: null,
            status: u.status,
            vehicleType: u.metadata?.vehicleType,
            vehiclePlate: u.metadata?.vehiclePlate,
            profilePicture: u.metadata?.profilePicture,
            metadata: u.metadata || {},
            createdAt: u.createdAt || null,
            updatedAt: u.updatedAt || null,
            token: u.token || null,
          },
        }
      }
    }

    if (method === 'PATCH' || method === 'POST') {
      const u = raw.data || {}
      const updated = {
          id: u.id,
          userId: u.id,
          name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          email: u.email?.toLowerCase(),
          countryCode: u.countryCode || null,
          phoneNumber: u.metadata?.phoneNumber || u.phoneNumber || '',
          password: null,
          status: u.status,
          vehicleType: u.metadata?.vehicleType,
          vehiclePlate: u.metadata?.vehiclePlate,
          profilePicture: u.metadata?.profilePicture,
          metadata: u.metadata || {},
          createdAt: u.createdAt || null,
          updatedAt: u.updatedAt || null,
        }
      return {
        statusCode: raw.statusCode || 200,
        message: raw.message || 'Profil mis à jour',
        user: updated,
        data: updated,
      }
    }

    if (method === 'GET') {
      const u = raw.data || {}
      const res = {
          id: u.id,
          userId: u.id,
          name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          email: u.email?.toLowerCase(),
          countryCode: u.countryCode || null,
          phoneNumber: u.metadata?.phoneNumber || u.phoneNumber || '',
          password: null,
          status: u.status,
          vehicleType: u.metadata?.vehicleType,
          vehiclePlate: u.metadata?.vehiclePlate,
          profilePicture: u.metadata?.profilePicture,
          metadata: u.metadata || {},
          createdAt: u.createdAt || null,
          updatedAt: u.updatedAt || null,
        }
      return {
        statusCode: raw.statusCode || 200,
        message: raw.message || 'ok',
        user: res,
        data: res,
      }
    }

    // Fallback pour format hérité
    return {
      email: raw.email,
      countryCode: raw.countryCode,
      createdAt: raw.createdAt,
      firstName: raw.firstName,
      id: raw.id,
      lastName: raw.lastName,
      password: raw.password,
      phoneNumber: raw.phoneNumber,
      updatedAt: raw.updatedAt,
      currency: raw.currency,
      isLocked: raw.isLocked,
      payoutLocked: raw.payoutLocked,
      transactionLocked: raw.transactionLocked,
      lockingReason: raw.lockingReason,
    }
  }
}
